// import { MoveAndDraw } from './demos/move-and-draw.js'
import { SpaceToDraw } from './demos/space-to-draw.js'
import { AudioDrawing } from './demos/audio-drawing.js'
import { DropToDraw } from './demos/drop-to-draw.js'
import { Rotate3d } from './demos/rotate-3d.js'
import { ClickToSwitch } from './demos/click-to-switch.js'
import { MoveToGravitate } from './demos/move-to-gravitate.js'

export const projects = [
  {
    name: 'Patternizify',
    fields: ['Code', 'Tool', 'JavaScript', 'Grid', 'Pixel', 'Patterns'],
    tags: ['code'],
    link: 'https://patternizify.studiodev.xyz/',
    description: {
      main: `Patternizify is a little web-based tool to create patterns on a grid & preview it live; the size, colors and inputs to generate and edit the pattern are all customizable.`,
      secondary: `I initially developed this tool to play around & design patterns to do peyote stitch bracelets, but it can actually be used for any weaving practice, to be displayed on the web, printed, and probably many other usages! The pattern tile can be downloaded in SVG format, and is for example used here on the website as a repeating background image set in CSS.`,
    },
    preview: true,
    visuals: [
      'mov',
      'jpg',
      'png',
      'mov',
      'jpg',
      'mov',
      'png',
      'png',
      'png',
      'png',
      'png',
      'png',
      'png',
    ],
  },
  {
    name: 'ASCIIzify',
    fields: ['Code', 'Tool', 'JavaScript', 'ASCII art', 'Image processing'],
    tags: ['code'],
    link: 'https://asciizify.studiodev.xyz/',
    description: {
      main: `ASCIIzify is a web-based tool coded in ReactJS that redraws a given image in ASCII art using a set of characters as pixels ; the output is fully customizable by the user with different settings.`,
    },
    preview: true,
    visuals: ['jpg', 'png', 'jpg', 'jpg', 'jpg', 'png', 'jpg'],
  },
  {
    name: 'ASCIIzify Foil',
    fields: ['Paper', 'Hot foil', 'Printing'],
    tags: ['design'],
    description: {
      main: `ASCIIzify Foil is an experiment derived from [ASCIIzify](https://marie.studiodev.xyz/ASCIIzify) web app, printing the generated ASCII image on paper with hot holographic foil.`,
    },
    preview: true,
    visuals: ['jpg', 'mp4', 'jpg', 'mp4', 'jpg'],
  },
  {
    name: 'Symphony No. 404',
    fields: ['Code', 'JavaScript', 'Devtools', 'Browser console'],
    tags: ['code'],
    link: 'https://symphony-404.studiodev.xyz/',
    description: {
      main: `Symphony No. 404 is a generator that throws, catches and displays errors in the browser developer tools console, creating a visual rhythmic symphony! The speed and the steps amount of errors generated can be customized, changing the rhythm pattern.`,
    },
    preview: true,
    visuals: [
      'jpg',
      'mov',
      'mov',
      'png',
      'jpg',
      'jpg',
      'mov',
      'png',
      'png',
      'jpg',
      'jpg',
      'png',
      'png',
    ],
  },
  {
    name: 'Displacizify',
    fields: ['Code', 'JavaScript', 'PixiJS', 'Displacement map', 'Filter'],
    tags: ['code'],
    link: 'https://displacizify.studiodev.xyz/',
    description: {
      main: `A web app playing around with displacement maps & filters to animate images on mouse move with PixiJS library, following a course from [Superhi](https://www.superhi.com/).`,
    },
    preview: true,
    visuals: [
      'mov',
      'png',
      'jpg',
      'jpg',
      'png',
      'jpg',
      'jpg',
      'png',
      'jpg',
      'jpg',
    ],
  },
  {
    name: 'Anamorphosis',
    fields: [
      'Code',
      'Tool',
      'JavaScript',
      'Three.js',
      '3D',
      'Image processing',
    ],
    tags: ['code'],
    link: 'https://anamorphosis.studiodev.xyz/',
    description: {
      main: `Anamorphosis is a web-based app that extracts the green pixels of an image and translates them to reflective spheres dispatched in a 3 dimensional space.
If you set the camera point of view exactly in front of the scene, you see the anamorphosis of the image, which is seen as flat. 
This project was made for the challenge #02 of [@making_shit_together_is_cooler](https://www.instagram.com/making_shit_together_is_cooler), a creative community with monthly collaborative challenges.`,
    },
    preview: true,
    visuals: ['mov', 'png', 'png', 'jpg', 'png', 'jpg', 'jpg'],
  },
  {
    name: 'Blendizify Paper',
    fields: ['Vectorial', 'Path', 'Paper', 'Plotter', 'Cut', 'Draw'],
    tags: ['design'],
    description: {
      main: `Blendizify Paper is an experiment derived from [Blendizify](https://marie.studiodev.xyz/blendizify) web app, making a variation of layering different images together to create a new visual where the images are turned into vectorial paths and then either cut or drawn with a plotter on paper.`,
      // This project was made for the challenge #03 of [@making_shit_together_is_cooler](https://www.instagram.com/making_shit_together_is_cooler), a creative community with monthly collaborative challenges.`,
    },
    preview: true,
    visuals: [
      'jpg',
      'png',
      'jpg',
      'png',
      'mp4',
      'jpg',
      'jpg',
      'mp4',
      'jpg',
      'jpg',
    ],
  },
  {
    name: 'Blendizify',
    fields: ['Code', 'Tool', 'JavaScript', 'Blend mode', 'Image processing'],
    tags: ['code'],
    link: 'https://blendizify.studiodev.xyz/',
    description: {
      main: `Blendizify is a web-based tool to create visuals by layering several images — desaturated, over-contrasted & blended together with the difference mode.
      This project was made for the challenge #03 of [@making_shit_together_is_cooler](https://www.instagram.com/making_shit_together_is_cooler), a creative community with monthly collaborative challenges.`,
      secondary: `Blendizify has 2 views: the playground allows to compose with the different images available, and the inventory indexes all the possible results of mixing each of the images with the other ones.`,
    },
    preview: true,
    visuals: ['mov', 'png', 'png', 'jpg', 'jpg', 'png'],
  },
  {
    name: 'Mise en abyme',
    fields: ['Code', 'Web', 'JavaScript', 'Tool', 'Image processing'],
    tags: ['code'],
    link: 'https://mise-en-abyme.studiodev.xyz/',
    description: {
      main: `A web-based tool to redraw a given image with a set of pictures acting as pixels ; the output is customizable by the user with different settings & filters. 
      This project was made for the challenge #01 of [@making_shit_together_is_cooler](https://www.instagram.com/making_shit_together_is_cooler), a creative community with monthly collaborative challenges.`,
    },
    preview: true,
    visuals: [
      'png',
      'jpg',
      'mp4',
      'jpg',
      'jpg',
      'mp4',
      'jpg',
      'jpg',
      'jpg',
      'jpg',
      'jpg',
      'jpg',
      'mov',
      'jpg',
      'png',
      'png',
      'mp4',
      'jpg',
      'mp4',
    ],
  },
  {
    name: 'Kikoo Playground',
    fields: ['Code', 'Interaction', 'JavaScript', 'CSS'],
    tags: ['code', 'design'],
    link: 'https://kikoo-playground.studiodev.xyz/',
    description: {
      main: `Kikoo Playground is an experimental personal project presented as a website displaying a bunch of interactive blocks to draw, play, write, animate...`,
    },
    preview: true,
    visuals: [
      'png',
      'gif',
      'jpg',
      'mov',
      'png',
      'gif',
      'png',
      'png',
      'gif',
      'png',
      'gif',
    ],
  },
  {
    name: 'Generative patterns',
    fields: ['Code', 'Electronics', 'Arduino'],
    tags: ['code', 'electronics'],
    link: 'https://generative-patterns.studiodev.xyz/',
    description: {
      main: `A website to generate random patterns from 24 geometrical shapes inspired by cymatics ; further experiments with an Arduino turned into a capacitive sensor to trigger the generation of a random pattern at each touch.`,
    },
    preview: true,
    visuals: [
      'jpg',
      'png',
      'jpg',
      'jpg',
      'jpg',
      'jpg',
      'jpg',
      'jpg',
      'jpg',
      'jpg',
      'jpg',
      'jpg',
      'mp4',
      'jpg',
      'jpg',
      'jpg',
      'jpg',
      'jpg',
    ],
  },
  {
    name: 'Bioplastic light cakes',
    fields: ['Code', 'Bioplastic', 'Electronics', 'Arduino'],
    tags: ['code', 'electronics'],
    description: {
      main: `Experimentations with alganyl bioplastic homemade cakes, used as conductive sensors to light up a LED with an Arduino board.`,
    },
    preview: true,
    visuals: [
      'jpg',
      'mov',
      'jpg',
      'jpg',
      'mov',
      'jpg',
      'mov',
      'jpg',
      'jpg',
      'mov',
    ],
  },
  {
    name: 'Tactile drawer',
    fields: ['Code', 'Electronics', 'Arduino'],
    tags: ['code', 'electronics'],
    description: {
      main: `Drawing with colors on the browser by touching conductive aluminium strips.`,
    },
    preview: true,
    visuals: ['mov', 'mov', 'mov', 'mov'],
  },
  {
    name: 'Dom JS',
    fields: ['Code', 'Pedagogy', 'HTML', 'JavaScript', 'CSS'],
    tags: ['code'],
    link: 'https://www.youtube.com/playlist?list=PLHyAJ_GrRtf9Gyas7GswuMUlucriwYmXU',
    description: {
      main: `Dom JS consists in a series of exercises to learn HTML, CSS & JS in a fun way. 
      Some videos have been recorded to help & guide the learners.
      This pedagogical contents are part of the [Zone 01](https://marie.studiodev.xyz/zone-01) project on which I'm working since 2018.`,
    },
    preview: true,
    visuals: ['png', 'png', 'png', 'png', 'png', 'png', 'png'],
  },
  {
    name: 'Split flap book',
    fields: ['Code', 'Cricut', 'Electronics'],
    tags: ['code', 'electronics'],
    description: {
      main: `Prototype of a DIY split flap device unit, used as a flip book displaying an animation generated with code ; the spool is 3D printed & the plastic sheets have been cut with a plotter.`,
    },
    preview: true,
    visuals: ['jpg', 'jpg', 'mov', 'mov', 'mov', 'png', 'gif', 'mp4'],
  },
  {
    name: 'Atomizify',
    fields: ['Code', 'NPM package', 'JavaScript', 'CSS'],
    tags: ['code'],
    link: 'https://www.npmjs.com/package/atomizify',
    description: {
      main: `[Atomizify](https://atomizify.studiodev.xyz/) is a small JavaScript library that generates a bunch of atomic CSS classes. 
      Atomizify can fit all types of developers, from lazy to geekiest ones! 
      It provides a few options to play with it: set some filters, choose your typecase system, or add your own custom classes.`,
    },
    preview: true,
    visuals: ['png', 'jpg', 'jpg', 'png'],
  },
  {
    name: 'Variable font',
    fields: ['Code', 'Arduino', 'Variable font', 'Animation', 'Electronics'],
    tags: ['code', 'electronics'],
    description: {
      main: `Color variable font with geometric shapes created during a workshop.
      The weight axis is animated in several ways — linearly in videos, and with different kinds of input: a luminosity sensor connected to an Arduino board, and the mouse position in the webpage`,
    },
    preview: true,
    visuals: ['jpg', 'mov', 'mov', 'jpg', 'mov', 'mov', 'mov'],
  },
  {
    name: '1 week 4 nuggets',
    fields: ['Code', 'Inspiration', 'JavaScript', 'Index'],
    tags: ['code', 'design'],
    link: 'https://1week4nuggets.studiodev.xyz/',
    description: {
      main: `1 week, 4 nuggets is an index for creative inspirations ; every week, 4 cool items are added.`,
    },
    preview: true,
    visuals: [
      'png',
      'png',
      'png',
      'png',
      'png',
      'png',
      'png',
      'png',
      'png',
      'png',
    ],
  },
  {
    name: 'Shader land',
    fields: ['Code', 'Animation', 'Shaders'],
    tags: ['code'],
    description: {
      main: `Workshop on GLSL shaders at the Gaïté Lyrique in Paris — initiation to live coding & exploration of the raymarching technique.`,
    },
    preview: true,
    visuals: ['png', 'mov', 'png', 'mov', 'png', 'mov', 'png'],
  },
  {
    name: 'Makey Makey x knob',
    fields: ['Code', 'Arduino', 'Web', 'Animation', 'Electronics'],
    tags: ['code', 'electronics'],
    preview: true,
    description: {
      main: `A knob plugged on a Makey Makey board serves as input to control the size and color of a circle on a webpage.`,
    },
    visuals: ['mp4'],
  },
  {
    name: 'Rotate 3D',
    tags: ['demo', 'code'],
    demo: Rotate3d,
    instructions: `Move the mouse to rotate.
    Click to change the effect.`,
  },
  {
    name: 'Click to switch',
    tags: ['demo', 'code'],
    demo: ClickToSwitch,
    instructions: `Move the mouse to animate. 
    Click on options to switch.`,
  },
  {
    name: 'Move to gravitate',
    tags: ['demo', 'code'],
    demo: MoveToGravitate,
    instructions: `Move the mouse to affect the gravity.
    Click to change the circles' size.`,
  },
  {
    name: 'Drop to draw',
    tags: ['demo', 'code'],
    demo: DropToDraw,
    instructions: `Drop an image to generate a drawing.
    Press S to save as image.`,
  },
  {
    name: 'Speak to draw',
    tags: ['demo', 'code'],
    demo: AudioDrawing,
    instructions: `Allow your mic.
    Make some noise to draw.
    Press S to save as image.`,
  },
  // {
  //   name: 'Move and draw',
  //   tags: ['demo', 'code'],
  //   demo: MoveAndDraw,
  //   instructions: `Move the mouse to draw.
  //   Click to clear.
  //   Press S to save as image.`,
  // },
  {
    name: 'Space to draw',
    tags: ['demo', 'code'],
    demo: SpaceToDraw,
    instructions: `Press Space continuously to draw step by step.
    Press Enter to draw all at a time.
    Press C to clear.
    Press S to save as image.`,
  },
  {
    name: 'Kikoo Docteur',
    fields: ['Visual identity'],
    tags: ['design'],
    baseline: `Awareness campaign about medical information online. Creation of posters, 3D cards, a website, a Youtube spot & pop-up ads.`,
    description: {
      main: `Kikoo Docteur is my diploma project during my Master Degree at the LAAB Rennes.`,
      secondary: `It's an awareness campaign to sensitize young people surfing on the web against the flood of bad information about health that can be found on the Internet.
        
        As the main target is youth, the tone is fun & friendly and the visual language is a combination of the medical universe with inherent web expressions, images & syntaxes.`,
    },
    preview: true,
    visuals: ['gif', 'jpg', 'gif', 'jpg', 'gif', 'gif'],
  },
  {
    name: 'Zone 01',
    fields: ['Interface', 'React', 'Node', 'GraphQL'],
    link: 'https://zone01.gr/',
    tags: ['design', 'code'],
    baseline: `Pedagogic platform to learn programming. Conception, interface design, data architecture and React + GraphQL development of the platform.`,
    description: {
      main: `Zone 01 is a worldwide education project developped and led by Nicolas Sadirac, based on his innovative pedagogy model of peer-learning that he formerly implemented in French schools such as Epita, Epitech and 42.`,
      secondary: `This ambitious initiative aims to spread digital science through a contemporary method of learning, which revolutionizes the conventional descendant ways of aquiring knowledge ; schools all around the globe but no teachers, only students sharing and bringing their own experience to help each other and build common intelligence.
        
        The heart of the project is the software deployed in the schools, which consists in a platform the student uses during his whole training in 01: a complete program filled with pedagogic contents that will lead him to learn programming concepts, develop projects, review his pairs' code and work in team.
        
        Within our team of 9 developers, I'm in charge of conceiving and designing the interface, as well as developing a consequent part of the front-end. To achieve this, I've built an entire components library, enabling my colleagues to easily integrate the design system in their work.`,
    },
    preview: true,
    visuals: ['jpg', 'jpg', 'jpg'],
  },
  {
    name: 'Mikme',
    fields: ['Visual identity', 'Webdesign', 'App design', 'Motion design'],
    tags: ['design'],
    baseline: `Start up developing a portable microphone. Branding, art direction, webdesign, app design & shooting.`,
    description: {
      main: `Mikme is a start up which developed a portable, wireless studio-grade microphone that users can record with anywhere.`,
      secondary: `I worked on this project for 4 months while I was intern at Bleed Vienna.
        
        Mikme came to us to create their whole brand identity, including logo lifting, global art direction, webdesign, app design, film footage & photo shooting.`,
    },
    preview: true,
    visuals: ['jpg', 'jpg'],
  },
  {
    name: 'Wix Playground Academy',
    fields: ['Visual identity', 'Webdesign', 'School', 'Portfolio'],
    link: 'https://www.wix.com/playground/academy',
    tags: ['design'],
    baseline: `Student of the Europe edition of the Wix Playground Academy 2022. Creative direction & webdesign.`,
    description: {
      main: `Wix Playground Academy is a 5-week intensive program that helps young designers to work on their values & build an outstanding portfolio to display their work ; I was a student in this second European promotion, among other talented emerging designers!`,
      secondary: `Below are displayed the final website screens, but also all the research achieved during the program: moodboards, values, wireframes, mockups, etc.

      The website is still a work in progress 🙊`,
    },
    preview: true,
    visuals: [
      'png',
      'png',
      'png',
      'png',
      'png',
      'png',
      'png',
      'jpg',
      'jpg',
      'png',
      'jpg',
      'jpg',
      'jpg',
      'jpg',
      'jpg',
      'jpg',
      'jpg',
      'jpg',
      'jpg',
      'jpg',
      'jpg',
      'jpg',
      'jpg',
      'jpg',
      'jpg',
      'jpg',
    ],
  },
  {
    name: 'Showcase 3D',
    fields: ['Visual identity', 'Webdesign', '3D', 'React'],
    tags: ['design', 'code'],
    description: {
      main: `Web experiments with the Three.js library to showcase my portfolio.`,
    },
    preview: true,
    visuals: ['mov', 'jpg', 'mov', 'mp4'],
  },
  {
    name: 'Atelier Rusch',
    fields: ['Webdesign', 'React JS development'],
    tags: ['design', 'code'],
    baseline: `Co-design studio. UX design, webdesign and React Javascript development of the site with an administrable back-office. Co-design studio. UX design, webdesign and React Javascript development of the site with an administrable back-office.`,
    description: {
      main: `Atelier Rusch is a co-design studio proposing creative methods to co-conceive new services for cities and companies.`,
      secondary: `This project, commissioned to the Wild Code School during my intensive JavaScript course, is constantly being enhanced with new features, as Atelier Rusch decided to hire me as their freelance developer.
        
        At the beginning of the project, as part of a team of 6 developers, I was in charge of the UX & graphic design, and took part in the front-end & back-end development with React. The website consists in a visible part displaying the activity of the studio, and a self-administrable back-office we develop from scratch.`,
    },
    preview: true,
    visuals: ['jpg', 'jpg'],
  },
  {
    name: 'portfolio research',
    fields: ['Code', 'Visual identity', 'React', 'JavaScript'],
    tags: ['design', 'code'],
    description: {
      main: `Here are a bunch of stuff I experimented while I was researching for the look of this website, that I didn't choose to finally keep.
      But here they end up, as I find those interesting to see the process & how I got to the final result.`,
    },
    preview: true,
    visuals: ['gif', 'png', 'png', 'png', 'png', 'png', 'png'],
  },
  {
    name: 'Open opéra',
    fields: ['Interface design'],
    tags: ['design'],
    baseline: `Public institution of Paris. Creation of an interactive platform about the singing and the comic opera.`,
    description: {
      main: `Open Opéra is a project commissioned during my Master Degree at La Sorbonne by l'Opéra comique, one of the oldest French dramatic and musical institutions in Paris.`,
      secondary: `The aim of this interactive platform is to learn & share online how to sing classical patterns of the comic opera.`,
    },
    preview: true,
    visuals: ['gif', 'jpg', 'gif', 'jpg'],
  },
  {
    name: 'La gaïté nomade',
    fields: ['Visual identity', 'Generative drawing'],
    tags: ['design'],
    baseline: `Institution around digital cultures. Creation of an evolutive identity with a declinable logo and illustrations generated by Processing.`,
    description: {
      main: `La Gaité Nomade is a project commissioned during my Master Degree at La Sorbonne by La Gaîté Lyrique, a Parisian cultural venue dedicated to the meeting between arts, technologies, and society.`,
      secondary: `As the pitch was to develop a visual concept for their outdoor events, I created an evolutive logo and generative illustrations with Processing.`,
    },
    preview: true,
    visuals: ['gif', 'jpg', 'jpg', 'jpg', 'jpg', 'jpg'],
  },
  {
    name: 'Créations & patrimoines',
    fields: ['Visual identity', 'Editorial design', 'Webdesign'],
    tags: ['design'],
    baseline: `Exhibitions of contemporary art in heritage places. Redesign of the visual identity, editorial contents & website.`,
    description: {
      main: `Créations & Patrimoines is a French cultural entity which organizes contemporary art exhibitions in stunning heritage places.`,
      secondary: `For each event, a printed publication is released.
        The project, commissioned during my Master Degree at La Sorbonne, was to redesign their website, identity and editorial contents in order to promote and expand the concept.`,
    },
    preview: true,
    visuals: ['gif', 'jpg', 'jpg'],
  },
  {
    name: 'The Peacock society',
    fields: ['Poster design', 'Motion design'],
    tags: ['design'],
    baseline: `Festival of techno music. Creation of posters and a video teaser.`,
    description: {
      main: `The Peacock Society is a techno music festival which takes place twice a year in Paris.`,
      secondary: `The event offers a musical programming I particularly appreciate, what made me create some posters and a video teaser for the next edition with images I altered through a Processing script.`,
    },
    preview: true,
    visuals: ['jpg', 'jpg'],
  },
  {
    name: 'Plastik',
    fields: ['Visual identity', 'Webdesign'],
    tags: ['design'],
    baseline: `Art & science journal. Redesign of the website & the logo.`,
    description: {
      main: `Plastik is a journal about Art & Science published by the CNRS research team at La Sorbonne.`,
      secondary: `They commissioned our promotion to redesign the website and the logo, and to migrate all their printed contents online.`,
    },
    preview: true,
    visuals: ['jpg', 'jpg'],
  },
]
